/**
 * The Reports hub's SHARED filter — report id + `[from, to]` month range +
 * account selection — and its two persistence layers.
 *
 * Why this lives outside `Reports.tsx`: the route file needs `isReportId` and
 * the month validator for `validateSearch`, and importing the screen module
 * just to reach them would pull the whole report tree into route evaluation.
 *
 * Persistence, in precedence order:
 *
 *   1. **URL search params** — the source of truth while the screen is mounted.
 *      The back button steps through filter changes and a link carries the
 *      exact report + range + accounts to someone else.
 *   2. **localStorage** — seeds the URL on a bare `/finance/reports` visit, so
 *      arriving from the sidebar restores the filter you last worked with
 *      instead of snapping back to last-12-months / all-accounts.
 *   3. **Defaults** — last 12 months ending this month, every account.
 *
 * The store is deliberately a single small blob under one key: unlike task
 * sorting there is exactly one filter for the whole screen, so there is nothing
 * to merge and a clobbering write is correct.
 */

import { addMonths, currentMonth, isValidMonth } from './report-format';

// ─── Report registry ──────────────────────────────────────────────────────────

/**
 * Every report in the hub, in switcher order. Grouped by the question each
 * answers: flow of money, then composition, then balances, then savings, then
 * the professional-recovery special case.
 */
export const REPORTS = [
  { id: 'cashflow', label: 'Cash flow' },
  { id: 'income-expense', label: 'Income & expense' },
  { id: 'period-comparison', label: 'Period comparison' },
  { id: 'category-trends', label: 'Category trends' },
  { id: 'balance-evolution', label: 'Account balance evolution' },
  { id: 'net-worth', label: 'Net worth' },
  { id: 'savings-flow', label: 'Savings flow' },
  { id: 'savings-rate', label: 'Savings rate' },
  { id: 'professional-recovery', label: 'Professional recovery' },
] as const;

export type ReportId = (typeof REPORTS)[number]['id'];

export function isReportId(v: unknown): v is ReportId {
  return typeof v === 'string' && REPORTS.some((r) => r.id === v);
}

// ─── Filter shape ─────────────────────────────────────────────────────────────

export interface ReportFilters {
  report: ReportId;
  /** Inclusive month range, `YYYY-MM`. */
  from: string;
  to: string;
  /** Selected account ids; EMPTY MEANS ALL (matches the analytics endpoints). */
  accounts: string[];
}

export function defaultFilters(): ReportFilters {
  const now = currentMonth();
  return { report: 'cashflow', from: addMonths(now, -11), to: now, accounts: [] };
}

/** A range is usable when both ends parse and aren't inverted. */
export function isValidRange(from: string, to: string): boolean {
  return isValidMonth(from) && isValidMonth(to) && from <= to;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Parse the `accounts` search param (comma-joined ids) into a clean array. */
export function parseAccountsParam(raw: unknown): string[] | undefined {
  if (typeof raw !== 'string') return undefined;
  if (raw === '') return [];
  const ids = raw.split(',').filter((id) => UUID_RE.test(id));
  return ids.length > 0 ? ids : [];
}

/** Serialize an account selection for the URL. Empty (= all) is omitted. */
export function accountsParam(accounts: string[]): string | undefined {
  return accounts.length > 0 ? accounts.join(',') : undefined;
}

// ─── localStorage ─────────────────────────────────────────────────────────────

const STORAGE_KEY = 'kos.finance.reportfilters.v1';

/**
 * Parse a persisted blob, dropping anything malformed. Takes the raw string
 * rather than reading storage itself so it can be asserted without a DOM.
 */
export function readStoredFilters(raw: string | null): Partial<ReportFilters> {
  if (!raw) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};

  const { report, from, to, accounts } = parsed as Record<string, unknown>;
  const out: Partial<ReportFilters> = {};
  if (isReportId(report)) out.report = report;
  // Only accept a range that is still coherent — a half-valid stored range
  // would otherwise disable every query on arrival with no visible cause.
  if (typeof from === 'string' && typeof to === 'string' && isValidRange(from, to)) {
    out.from = from;
    out.to = to;
  }
  if (Array.isArray(accounts) && accounts.every((a) => typeof a === 'string' && UUID_RE.test(a))) {
    out.accounts = accounts as string[];
  }
  return out;
}

export function loadStoredFilters(): Partial<ReportFilters> {
  if (typeof window === 'undefined') return {};
  try {
    return readStoredFilters(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    // localStorage unavailable (private mode, quota) — URL-only this session.
    return {};
  }
}

export function persistFilters(filters: ReportFilters): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // localStorage unavailable (private mode, quota) — URL-only this session.
  }
}
